import { MetadataLoader, PreviewLoader } from "@/lib/metadata/metadata-loader"
import { getFileForPhoto } from "../files-state"
import { ProjectItem } from "../project/projects-service"
import { CatalogManager, type CullingInfoRecord, type PhotoRecord } from "@/lib/catalog"
import { ClusterThreshold } from "@/lib/catalog/types"
import { createBasicPhotoMetadata, hasNoEmbeddedPreview } from "@/lib/catalog/utils"
import { PreviewSize } from "@/store/previews/previews-types"
import {
	cullingInfoAtom,
	updateCullingInfos,
	getPhotoMetadata,
	sortedPhotosAtom,
	allPhotosAtom,
	getCullingInfosCount,
	getCullingInfo,
	updatePhotoMetadata,
	photosAtom,
} from "../photo/photos-state"
import { store } from "../store"
import { Culler } from "@/lib/culling/culler"
import { createOrientedBitmap } from "@/core/utils/load-image"
import {
	clusterOnTheFlyForCullingAndEditing,
	mergeClusters,
	getSortedPhotosAndIndexMap,
} from "@/lib/ai/ai-cluster"
import { Sentry } from "@/telemetry/sentry"
import { PhotoId } from "../undoable-state"
import { CullingInputDimension } from "@/lib/ai/utils"
import { projectAtom } from "../project/projects-state"
import PQueue from "p-queue"
import { fetchOriginalPreview } from "@/storage/indexeddb"
import { isMetadataFinishedAtom } from "../app-state"
import { logClustering } from "@/lib/ai/utils"
import { logClusterSummary } from "@/lib/ai/ai-cluster"
import { transfer } from "@/utils/peer"

/**
 * EditClusteringImporter - Self-contained importer for deterministic clustering in edit mode
 *
 * Purpose: Extract ONLY deep cluster features and assign clusterLeaderId for style application
 * Triggered by: batchApplyStyle in editing mode
 *
 * Key characteristics:
 * - Self-contained: Always extracts metadata + preview directly if not available
 * - Independent: Not coordinated with metadataImporter
 * - Reuses culling features: If available from culling mode, just skip extraction
 * - Uses ClusterThreshold.EditClustering key in clusterLeaderIdMap
 * - Priority-based: Photos are processed in the fixed order (filename → timestamp when available)
 */
class EditClusteringImporter {
	private bufferFlushToUILimit = 10

	// Single queue for clustering extraction (no priority changes for deterministic order)
	private clusteringQueue = new PQueue({ concurrency: 1, autoStart: true })

	// Buffer to collect clustering data before flushing to UI
	private collectedClusteringBuffer = new Map<PhotoId, CullingInfoRecord>()

	// Callback for when a photo is processed (clustered or marked as no-preview)
	private onPhotoProcessedCallback?: () => void

	// Track which photos need clustering (for priority updates)
	private photosToClusterIds: PhotoId[] = []
	private metadataFinishUnsubscribe?: () => void

	// External data
	public startProjectId: string = ""
	private _isInProgress = false
	private clusteringExtractedCount = 0
	private totalPhotosToCluster = 0
	private prioritizeImageIdsSet = new Set<PhotoId>()

	/**
	 * Check if clustering is currently in progress
	 */
	public get isInProgress(): boolean {
		return this._isInProgress
	}

	/**
	 * Start pre-edit-clustering extraction in the fixed order (filename → timestamp when available)
	 * @param onPhotoProcessed - Optional callback fired after each photo is processed
	 */
	async start(
		project: ProjectItem,
		photosToCluster: PhotoId[],
		prioritizeImageIds: PhotoId[],
		onPhotoProcessed?: () => void,
	) {
		if (this._isInProgress) {
			console.warn("[editClusteringImporter] Already in progress, clearing previous tasks")
			this.clearTasks()
		}

		this.startProjectId = project.id
		this._isInProgress = true
		this.clusteringExtractedCount = 0
		this.totalPhotosToCluster = photosToCluster.length
		this.photosToClusterIds = photosToCluster
		this.prioritizeImageIdsSet = new Set(prioritizeImageIds)
		this.onPhotoProcessedCallback = onPhotoProcessed

		// Fetch existing culling info from catalog if not already present
		if (getCullingInfosCount() === 0) {
			const cullingInfoRecords = await CatalogManager.fetchCullingInfoRecords()
			store.set(cullingInfoAtom, cullingInfoRecords)
			this.onPhotoProcessedCallback?.()
		}

		const allPhotos = store.get(sortedPhotosAtom)
		this.bufferFlushToUILimit = allPhotos.length > 4500 ? 15 : 10

		logClustering(`[editClusteringImporter] Starting for ${photosToCluster.length} photos`)

		/* Setup listener */
		this.clusteringQueue.removeAllListeners("empty")
		this.clusteringQueue.addListener("empty", () => {
			if (this.clusteringExtractedCount >= this.totalPhotosToCluster) {
				this.mergeClustersAndUpdateBuffer()
				this.flushCollectedClusteringBuffer()
				console.log(
					`[editClusteringImporter] Complete! ${this.clusteringExtractedCount} / ${this.totalPhotosToCluster}`,
				)
				logClusterSummary({
					cullingInfos: store.get(cullingInfoAtom),
					threshold: ClusterThreshold.EditClustering,
					prefix: "[editClustering]",
				})
				this._isInProgress = false
				this.metadataFinishUnsubscribe?.()
				// Safeguard: Ensure all photos are checked and queued when clustering finishes
				// This catches any photos that might not have been queued during processing
				this.onPhotoProcessedCallback?.()
			}
		})

		// Subscribe to metadata finish to reset priorities when all timestamps available
		this.metadataFinishUnsubscribe = store.sub(isMetadataFinishedAtom, () => {
			if (store.get(isMetadataFinishedAtom)) {
				logClustering(
					"[editClusteringImporter] Metadata finished - resetting priorities to timestamp order",
				)
				this.resetQueuePriorities()
			}
		})

		// Queue all photos with priorities based on current order
		this.queueAllPhotos()

		// Ensure queue is started
		if (this.clusteringQueue.isPaused) {
			this.clusteringQueue.start()
		}
	}

	cancel() {
		this.clusteringQueue.clear()
		void Culler.cleanupModels()
		this._isInProgress = false
		this.startProjectId = ""
		this.collectedClusteringBuffer.clear()
		this.clusteringExtractedCount = 0
		this.totalPhotosToCluster = 0
		this.photosToClusterIds = []
		this.metadataFinishUnsubscribe?.()
		this.metadataFinishUnsubscribe = undefined
	}

	async pauseAndWaitForInFlightTasksToComplete() {
		this.clusteringQueue.pause()
		await this.clusteringQueue.onPendingZero()
	}

	resumeIfNeeded() {
		if (this.clusteringQueue.size === 0) {
			return
		}

		this.clusteringQueue.start()
	}

	/**
	 * Async cancel that waits for in-flight tasks to complete before cleanup.
	 * Use this when you need to ensure no tasks are running after the call returns.
	 * @param cleanupModels - Whether to cleanup Culler models (default: true). Set to false when coordinating with other importers that share the same models.
	 */
	async pauseAndCancel({ cleanupModels = true } = {}) {
		// 1. Pause queue to stop starting new tasks
		this.clusteringQueue.pause()

		// 2. Clear pending tasks
		this.clusteringQueue.clear()

		// 3. Wait for any in-flight task to complete
		await this.clusteringQueue.onPendingZero()

		// 4. Cleanup
		if (cleanupModels) {
			void Culler.cleanupModels()
		}
		this._isInProgress = false
		this.startProjectId = ""
		this.collectedClusteringBuffer.clear()
		this.clusteringExtractedCount = 0
		this.totalPhotosToCluster = 0
		this.photosToClusterIds = []
		this.metadataFinishUnsubscribe?.()
		this.metadataFinishUnsubscribe = undefined
	}

	clearTasks() {
		// Pause queue first to ensure no tasks are currently running
		this.clusteringQueue.pause()
		this.clusteringQueue.clear()
		this.collectedClusteringBuffer.clear()
		this.clusteringExtractedCount = 0
		this.totalPhotosToCluster = 0
		this.prioritizeImageIdsSet.clear()
	}

	/**
	 * Extract ONLY clustering data (no scoring)
	 * Skips if photo already has EditClustering data
	 *
	 * Self-contained: Always extracts metadata and preview directly if not available
	 * Deterministic: Always extracts preview from metadata, never uses rendered previews
	 */
	private async extractClustering(photo: PhotoRecord) {
		// Fetch fresh data at execution time to ensure we have the latest state
		const cullingInfo = getCullingInfo(photo.id)

		// SKIP if already has clustering for EditClustering threshold (STANDARD PATTERN)
		if (cullingInfo?.clusterLeaderIdMap?.[ClusterThreshold.EditClustering]) {
			this.clusteringExtractedCount++
			return
		}

		// Also check if it was added to buffer while we were waiting
		if (this.collectedClusteringBuffer.has(photo.id)) {
			this.clusteringExtractedCount++
			return
		}

		if (store.get(projectAtom)?.id !== this.startProjectId) {
			return
		}

		try {
			// Skip hidden photos — no need to extract clustering data for them (same as no-preview photos)
			const currentPhoto = store.get(photosAtom)[photo.id]
			if (currentPhoto?.isHidden) {
				logClustering(`[editClusteringImporter] Skipping ${photo.id} - photo is hidden`)
				this.onPhotoProcessedCallback?.()
				return
			}

			const file = getFileForPhoto(photo)
			if (!file) {
				throw new Error("No file for photo")
			}

			let metadata = getPhotoMetadata(photo.id)

			// Extract metadata if not available or incomplete
			if (!metadata || !metadata.isComplete) {
				console.log(
					`[editClusteringImporter] Extracting metadata for ${photo.id} (usually already available)`,
				)
				metadata =
					(await MetadataLoader.getMetadata(file)).record ??
					createBasicPhotoMetadata(file, photo.id)
			}

			// Fast path: skip if we already know this photo has no embedded preview (persisted)
			if (hasNoEmbeddedPreview(metadata)) {
				logClustering(
					`[editClusteringImporter] Skipping ${photo.id} - already known to have no embedded preview`,
				)
				this.onPhotoProcessedCallback?.()
				return
			}

			// Fetch or extract preview directly from metadata (deterministic, never use rendered raw)
			const previewData = await this.getOrExtractPreviewData(photo)

			if (!previewData) {
				// No preview available (e.g., some DNGs) - can't cluster for edit mode
				// Persist this information so we don't re-extract on project reopen
				logClustering(
					`[editClusteringImporter] No preview for ${photo.id} - marking as non-clusterable`,
				)
				// Update metadata synchronously to persist hasEmbeddedPreview = false
				updatePhotoMetadata(photo.id, { ...metadata, hasEmbeddedPreview: false })
				// Notify that this photo is processed even if it won't update the cullingInfoAtom
				this.onPhotoProcessedCallback?.()
				// Note: clusteringExtractedCount will be incremented in finally block
				return
			}

			const bitmap = await createOrientedBitmap(previewData, metadata.orientation ?? null)

			// Extract clustering data ONLY (no scores)
			let clusteringData: CullingInfoRecord
			try {
				const [result] = await Culler.extractCullingData(
					transfer(
						[
							{
								photoId: photo.id,
								inputImage: bitmap,
								extractScores: false,
							},
						],
						[bitmap],
					),
				)
				if (!result) throw new Error("Culler returned no result")
				clusteringData = result.info
			} finally {
				bitmap.close()
			}

			// Assign clusterLeaderId using EditClustering threshold
			clusteringData = clusterOnTheFlyForCullingAndEditing(clusteringData, {
				...store.get(cullingInfoAtom),
				...Object.fromEntries(this.collectedClusteringBuffer.entries()),
			})
			logClustering(`[editClusteringImporter] Extracted clustering for ${photo.id}, 
				initial key photo: ${clusteringData.clusterLeaderIdMap?.[ClusterThreshold.EditClustering] ?? "none"}`)

			this.collectedClusteringBuffer.set(photo.id, clusteringData)

			// Flush buffer periodically
			if (this.collectedClusteringBuffer.size >= this.bufferFlushToUILimit) {
				this.mergeClustersAndUpdateBuffer()
				this.flushCollectedClusteringBuffer()
			}
		} catch (error) {
			Sentry.captureException(error, {
				tags: { description: "Error extracting edit clustering" },
			})
			console.error(`[editClusteringImporter] Error for ${photo.fileName}`, error)

			// Log more details about AggregateError
			if (error instanceof AggregateError) {
				console.error(
					`[editClusteringImporter] AggregateError has ${error.errors.length} errors:`,
				)
				error.errors.forEach((err, i) => {
					console.error(`  Error ${i + 1}:`, err)
				})
			}
		} finally {
			this.clusteringExtractedCount++
		}
	}

	private async getOrExtractPreviewData(photo: PhotoRecord) {
		const previewRecord = await fetchOriginalPreview({
			projectId: this.startProjectId,
			photoId: photo.id,
			size: PreviewSize.medium,
		})

		// If preview is already extracted (not rendered raw), return it
		if (previewRecord?.tags.includes("extracted") && previewRecord?.data) {
			return previewRecord?.data
		}

		let previewData: Blob | undefined

		const file = getFileForPhoto(photo)
		if (file) {
			// extract from metadata
			previewData =
				(await PreviewLoader.getImage(file, PreviewSize.medium, CullingInputDimension)) ??
				undefined
		}

		return previewData
	}

	private mergeClustersAndUpdateBuffer() {
		const updatedClusters = mergeClusters({
			...store.get(cullingInfoAtom),
			...Object.fromEntries(this.collectedClusteringBuffer.entries()),
		})

		for (const [id, value] of Object.entries(updatedClusters)) {
			this.collectedClusteringBuffer.set(id, value)
		}
	}

	private flushCollectedClusteringBuffer() {
		if (this.collectedClusteringBuffer.size === 0) {
			return
		}

		// Convert Map to array of entries, then clone (safer than cloning Map directly)
		const entries = Array.from(this.collectedClusteringBuffer.entries())
		const clonedEntries = structuredClone(entries)

		// Update store (this also stores to catalog)
		updateCullingInfos((infos) => {
			for (const [id, value] of clonedEntries) {
				infos[id] = value
			}
		}, true)

		// Only clear items that are already flushed
		for (const [id, _] of clonedEntries) {
			this.collectedClusteringBuffer.delete(id)
		}

		console.log(
			`[editClusteringImporter] Flushed ${clonedEntries.length} clustering records; ${this.clusteringExtractedCount}/${this.totalPhotosToCluster} photos extracted`,
		)

		// Notify after batch update (cullingInfoAtom has been updated)
		// This triggers checkAndQueuePendingPhotos to check if any photos are now ready
		this.onPhotoProcessedCallback?.()
	}

	/**
	 * Queue all photos with priorities based on fixed order (filename → timestamp)
	 */
	private queueAllPhotos() {
		const allPhotos = store.get(allPhotosAtom)
		const { sortedPhotos, idToIndex } = getSortedPhotosAndIndexMap(allPhotos)

		const photosToClusterSet = new Set(this.photosToClusterIds)
		const sortedPhotosToCluster = sortedPhotos.filter((p) => photosToClusterSet.has(p.id))

		console.log(
			`[editClusteringImporter] Queuing ${
				sortedPhotosToCluster.length
			} photos with priorities (metadata finished: ${store.get(isMetadataFinishedAtom)})`,
		)

		for (const photo of sortedPhotosToCluster) {
			let priority = sortedPhotosToCluster.length - (idToIndex[photo.id] ?? 0)
			if (this.prioritizeImageIdsSet.has(photo.id)) {
				priority = 10_000_000
			}

			// Add task with priority based on position in clustering order
			this.clusteringQueue.add(
				async () => {
					await this.extractClustering(photo)
				},
				{
					id: photo.id,
					priority,
				},
			)
		}
	}

	/**
	 * Reset queue priorities when metadata finishes (timestamps now available)
	 */
	private resetQueuePriorities() {
		console.log(
			"[editClusteringImporter] Metadata finished - updating priorities to timestamp order",
		)

		// Recalculate priorities based on timestamp order
		const allPhotos = store.get(allPhotosAtom)
		const { sortedPhotos, idToIndex } = getSortedPhotosAndIndexMap(allPhotos)

		const photosToClusterSet = new Set(this.photosToClusterIds)
		const sortedPhotosToCluster = sortedPhotos.filter((p) => photosToClusterSet.has(p.id))

		// Update priority for each pending task using setPriority
		let updatedCount = 0
		for (const photo of sortedPhotosToCluster) {
			let priority = sortedPhotosToCluster.length - (idToIndex[photo.id] ?? 0)
			if (this.prioritizeImageIdsSet.has(photo.id)) {
				priority = 10_000_000
			}
			try {
				this.clusteringQueue.setPriority(photo.id, priority)
				updatedCount++
			} catch (error) {
				// Task may have already completed, which is fine
			}
		}

		console.log(`[editClusteringImporter] Updated priorities for ${updatedCount} pending tasks`)
	}
}

export const editClusteringImporter = new EditClusteringImporter()
